/**
 * Team & Roles Service
 * Phase 11: User Roles & Permissions
 *
 * Handles team member management, role assignment, invitations,
 * and permission checking.
 */

import { supabase, toError } from '@/lib/supabase'
import { Sentry } from '@/lib/sentry'
import { profileService } from '@/services/profileService'
import type {
  QmsRole,
  TeamMember,
  UserRole,
  UserRoleInsert,
  UserRoleUpdate,
  TeamInvitation,
  TeamInvitationInsert,
  Permission,
  PermissionAuditLog,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  TeamStats,
  PermissionCheckResult,
} from '@/types/qms'
import type { TeamRole } from '@/types/database'

/**
 * Map QMS role to the legacy team_role on profiles.
 * This keeps both role systems in sync.
 */
function mapQmsRoleToTeamRole(qmsRole: QmsRole): TeamRole {
  switch (qmsRole) {
    case 'qms_owner':
      return 'owner'
    case 'implementation_admin':
      return 'admin'
    case 'reviewer':
      return 'viewer'
    default:
      return 'member'
  }
}

async function sendTeamInviteEmail(invitationId: string): Promise<{ status: number; message?: string; existingUser?: boolean; inviteUrl?: string }> {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (!token) {
    throw new Error('Authentication session expired. Please sign in again and retry.')
  }

  const supabaseUrlRaw = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  if (!supabaseUrlRaw || !supabaseAnonKey) {
    throw new Error('Missing Supabase client configuration')
  }

  const supabaseUrl = supabaseUrlRaw
    .trim()
    .replace(/\s+/g, '')
    .replace(/%20/gi, '')
    .replace(/\/+$/, '')

  const response = await fetch(`${supabaseUrl}/functions/v1/send-team-invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ invitationId }),
  })

  let data: { status?: number; message?: string; existingUser?: boolean; inviteUrl?: string } | null = null
  try {
    data = await response.json()
  } catch {
    data = null
  }

  return {
    status: data?.status ?? response.status,
    message: data?.message,
    existingUser: data?.existingUser,
    inviteUrl: data?.inviteUrl,
  }
}

function getNextDeliveryAttempt(invitation: TeamInvitation): number {
  return Number.isFinite(invitation.delivery_attempts)
    ? invitation.delivery_attempts + 1
    : 1
}

async function markDeliverySuccess(
  invitation: TeamInvitation,
  attemptNumber: number,
  attemptedAt: string
): Promise<TeamInvitation | null> {
  const { data, error } = await supabase
    .from('team_invitations')
    .update({
      delivery_status: 'sent',
      delivery_error: null,
      delivery_attempts: attemptNumber,
      last_delivery_attempt_at: attemptedAt,
    })
    .eq('id', invitation.id)
    .select('*')
    .single()

  if (error) {
    Sentry.captureException(error, {
      tags: { area: 'team-invite', operation: 'delivery-tracking' },
      extra: { invitationId: invitation.id, attemptNumber },
    })
    return null
  }

  return data
}

async function markDeliveryFailure(
  invitation: TeamInvitation,
  attemptNumber: number,
  attemptedAt: string,
  message: string,
  operation: 'create' | 'resend',
  responseStatus?: number
): Promise<void> {
  const { error } = await supabase
    .from('team_invitations')
    .update({
      delivery_status: 'failed',
      delivery_error: message,
      delivery_attempts: attemptNumber,
      last_delivery_attempt_at: attemptedAt,
    })
    .eq('id', invitation.id)

  if (error) {
    Sentry.captureException(error, {
      tags: { area: 'team-invite', operation: 'delivery-tracking' },
      extra: {
        invitationId: invitation.id,
        attemptNumber,
        responseStatus,
        originalMessage: message,
      },
    })
  }

  Sentry.captureMessage('Team invite dispatch failed', {
    level: 'error',
    tags: { area: 'team-invite', operation },
    extra: {
      invitationId: invitation.id,
      invitationEmail: invitation.email,
      invitationRole: invitation.invited_role,
      attemptNumber,
      responseStatus,
      responseMessage: message,
    },
  })
}

async function dispatchTeamInvitation(
  invitation: TeamInvitation,
  operation: 'create' | 'resend'
): Promise<TeamInvitation & { existingUser?: boolean }> {
  const attemptNumber = getNextDeliveryAttempt(invitation)
  const attemptedAt = new Date().toISOString()
  let failureTracked = false

  try {
    const emailResult = await sendTeamInviteEmail(invitation.id)

    if (emailResult.status >= 400) {
      const message = emailResult.message || 'Failed to send invitation email'
      await markDeliveryFailure(invitation, attemptNumber, attemptedAt, message, operation, emailResult.status)
      failureTracked = true
      throw new Error(message)
    }

    const updatedInvitation = await markDeliverySuccess(invitation, attemptNumber, attemptedAt)
    if (!updatedInvitation) {
      throw new Error('Unable to record invitation delivery. Please try again.')
    }

    // P1 #6: Surface existingUser flag so UI can show appropriate message
    return { ...updatedInvitation, existingUser: emailResult.existingUser }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to send invitation email'

    if (!failureTracked) {
      await markDeliveryFailure(invitation, attemptNumber, attemptedAt, message, operation)
    }

    throw new Error(message)
  }
}

// ============================================
// TEAM MEMBERS
// ============================================

/**
 * Get all team members for the current organization with their roles
 */
export async function getTeamMembers(): Promise<TeamMember[]> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return []

  // Try with user_roles join first, fall back to profiles-only if table doesn't exist
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      full_name,
      avatar_url,
      created_at,
      role,
      user_roles!user_roles_user_id_profiles_fkey (
        role,
        assigned_at,
        is_active
      )
    `)
    .eq('organization_id', orgId)
    .order('full_name', { ascending: true })

  if (error) {
    // If user_roles table doesn't exist, query without it
    if (error.message?.includes('user_roles') || error.code === 'PGRST200' || error.code === '42P01') {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          avatar_url,
          created_at,
          role
        `)
        .eq('organization_id', orgId)
        .order('full_name', { ascending: true })

      if (fallbackError) throw fallbackError

      return (fallbackData || []).map((profile) => ({
        id: profile.id,
        full_name: profile.full_name,
        email: null,
        avatar_url: profile.avatar_url,
        qms_role: profile.role || null,
        role_assigned_at: null,
        is_role_active: true,
        last_login_at: null,
        created_at: profile.created_at,
      }))
    }
    throw toError(error)
  }

  // Look up emails from accepted invitations for members without names
  const memberIds = (data || []).map((p) => p.id)
  let emailByUserId = new Map<string | null, string>()
  if (memberIds.length > 0) {
    const { data: acceptedInvites } = await supabase
      .from('team_invitations')
      .select('accepted_by, email')
      .eq('organization_id', orgId)
      .eq('status', 'accepted')
      .in('accepted_by', memberIds)

    emailByUserId = new Map(
      (acceptedInvites || []).map((inv) => [inv.accepted_by, inv.email])
    )
  }

  // Transform the data to flatten user_roles
  return (data || []).map((profile) => {
    const userRoles = (profile as Record<string, unknown>).user_roles as Array<{ role: string; assigned_at: string; is_active: boolean }> | null
    const activeRole = userRoles?.find(
      (r: { is_active: boolean }) => r.is_active
    )
    return {
      id: profile.id,
      full_name: profile.full_name,
      email: emailByUserId.get(profile.id) || null,
      avatar_url: profile.avatar_url,
      qms_role: activeRole?.role || profile.role || null,
      role_assigned_at: activeRole?.assigned_at || null,
      is_role_active: activeRole?.is_active || false,
      last_login_at: null,
      created_at: profile.created_at,
    }
  })
}

/**
 * Get a single team member by ID
 */
export async function getTeamMember(userId: string): Promise<TeamMember | null> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return null

  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      full_name,
      avatar_url,
      created_at,
      role,
      user_roles!user_roles_user_id_profiles_fkey (
        role,
        assigned_at,
        is_active
      )
    `)
    .eq('id', userId)
    .eq('organization_id', orgId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    // Fallback without user_roles
    if (error.message?.includes('user_roles') || error.code === 'PGRST200' || error.code === '42P01') {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, created_at, role')
        .eq('id', userId)
        .eq('organization_id', orgId)
        .single()

      if (fallbackError) {
        if (fallbackError.code === 'PGRST116') return null
        throw fallbackError
      }

      return {
        id: fallbackData.id,
        full_name: fallbackData.full_name,
        email: null,
        avatar_url: fallbackData.avatar_url,
        qms_role: fallbackData.role || null,
        role_assigned_at: null,
        is_role_active: true,
        last_login_at: null,
        created_at: fallbackData.created_at,
      }
    }
    throw toError(error)
  }

  const userRoles = (data as Record<string, unknown>).user_roles as Array<{ role: string; assigned_at: string; is_active: boolean }> | null
  const activeRole = userRoles?.find(
    (r: { is_active: boolean }) => r.is_active
  )

  return {
    id: data.id,
    full_name: data.full_name,
    email: null,
    avatar_url: data.avatar_url,
    qms_role: activeRole?.role || data.role || null,
    role_assigned_at: activeRole?.assigned_at || null,
    is_role_active: activeRole?.is_active || false,
    last_login_at: null,
    created_at: data.created_at,
  }
}

/**
 * Get team statistics
 */
export async function getTeamStats(): Promise<TeamStats> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return { total_members: 0, by_role: {}, pending_invitations: 0, recent_role_changes: 0, expiring_roles: 0 }

  // Get member count - try user_roles first, fall back to profiles
  let totalMembers = 0
  const byRole: Partial<Record<QmsRole, number>> = {}

  const { data: members, error: membersError } = await supabase
    .from('user_roles')
    .select('role, is_active')
    .eq('organization_id', orgId)
    .eq('is_active', true)

  if (membersError) {
    // Table may not exist - fall back to profiles count
    const { count } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', orgId)
    totalMembers = count || 0
  } else {
    totalMembers = members?.length || 0
    for (const member of members || []) {
      if (member.is_active) {
        byRole[member.role as QmsRole] = (byRole[member.role as QmsRole] || 0) + 1
      }
    }
  }

  // Get pending invitations count (graceful if table missing)
  let pendingInvitations = 0
  const { count: invCount, error: invError } = await supabase
    .from('team_invitations')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('status', 'pending')

  if (!invError) pendingInvitations = invCount || 0

  // Get recent role changes (graceful if table missing)
  let recentChanges = 0
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  const { count: changesCount, error: changesError } = await supabase
    .from('permission_audit_log')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .in('action_type', ['role_assigned', 'role_removed', 'role_updated'])
    .gte('created_at', thirtyDaysAgo.toISOString())

  if (!changesError) recentChanges = changesCount || 0

  // Get expiring roles (graceful if table missing)
  let expiringRoles = 0
  const fourteenDaysFromNow = new Date()
  fourteenDaysFromNow.setDate(fourteenDaysFromNow.getDate() + 14)

  const { count: expiringCount, error: expiringError } = await supabase
    .from('user_roles')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .eq('is_active', true)
    .not('valid_until', 'is', null)
    .lte('valid_until', fourteenDaysFromNow.toISOString())

  if (!expiringError) expiringRoles = expiringCount || 0

  return {
    total_members: totalMembers,
    by_role: byRole,
    pending_invitations: pendingInvitations,
    recent_role_changes: recentChanges,
    expiring_roles: expiringRoles,
  }
}

// ============================================
// USER ROLES
// ============================================

/**
 * Get user role for a specific user
 */
export async function getUserRole(userId: string): Promise<UserRole | null> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return null

  // Fetch role without FK joins to avoid PostgREST hint issues
  const { data, error } = await supabase
    .from('user_roles')
    .select('*')
    .eq('user_id', userId)
    .eq('organization_id', orgId)
    .eq('is_active', true)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw toError(error)
  }

  // Fetch related profiles separately
  const profileIds = [data.user_id, data.assigned_by].filter(Boolean) as string[]
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name')
    .in('id', profileIds)

  const profileMap = new Map((profiles || []).map(p => [p.id, p]))
  const userProfile = profileMap.get(data.user_id)
  const assignedByProfile = data.assigned_by ? profileMap.get(data.assigned_by) : null

  return {
    ...data,
    user: userProfile ? { id: userProfile.id, full_name: userProfile.full_name, email: null } : null,
    assigned_by_user: assignedByProfile ? { id: assignedByProfile.id, full_name: assignedByProfile.full_name, email: null } : null,
  }
}

/**
 * Get all user roles for the organization
 */
export async function getUserRoles(): Promise<UserRole[]> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return []

  // Fetch roles without FK joins to avoid PostgREST hint issues
  const { data, error } = await supabase
    .from('user_roles')
    .select('*')
    .eq('organization_id', orgId)
    .order('assigned_at', { ascending: false })

  if (error) throw toError(error)
  if (!data || data.length === 0) return []

  // Fetch all related profiles in one query
  const profileIds = [...new Set(data.flatMap(r => [r.user_id, r.assigned_by].filter(Boolean)))]
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name')
    .in('id', profileIds)

  const profileMap = new Map((profiles || []).map(p => [p.id, p]))

  return data.map(role => {
    const userProfile = profileMap.get(role.user_id)
    const assignedByProfile = role.assigned_by ? profileMap.get(role.assigned_by) : null
    return {
      ...role,
      user: userProfile ? { id: userProfile.id, full_name: userProfile.full_name, email: null } : null,
      assigned_by_user: assignedByProfile ? { id: assignedByProfile.id, full_name: assignedByProfile.full_name, email: null } : null,
    }
  })
}

/**
 * Assign a role to a user
 */
export async function assignUserRole(data: UserRoleInsert): Promise<UserRole> {
  // Resolve organization_id: use provided value or fall back to current user's org
  const orgId = data.organization_id || await profileService.getOrganizationId()
  if (!orgId) throw new Error('Unable to resolve organization for role assignment')

  // Get current user for assigned_by
  const { data: { user } } = await supabase.auth.getUser()

  const { data: existing, error: existingError } = await supabase
    .from('user_roles')
    .select('id')
    .eq('user_id', data.user_id)
    .eq('organization_id', orgId)
    .maybeSingle()

  if (existingError) throw toError(existingError)

  let newRole: UserRole

  if (existing) {
    // Update the existing record rather than inserting (unique constraint on org+user)
    const { data: updated, error } = await supabase
      .from('user_roles')
      .update({
        role: data.role,
        assigned_by: user?.id || null,
        assigned_at: new Date().toISOString(),
        valid_from: data.valid_from || new Date().toISOString(),
        valid_until: data.valid_until ?? null,
        is_active: data.is_active ?? true,
        notes: data.notes ?? null,
      })
      .eq('id', existing.id)
      .select('*')
      .single()

    if (error) throw toError(error)
    newRole = updated
  } else {
    // No existing record — insert new
    const { data: inserted, error } = await supabase
      .from('user_roles')
      .insert({
        ...data,
        organization_id: orgId,
        assigned_by: user?.id || null,
        assigned_at: new Date().toISOString(),
        valid_from: data.valid_from || new Date().toISOString(),
        is_active: data.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw toError(error)
    newRole = inserted
  }

  // Sync legacy team_role on profiles
  const teamRole = mapQmsRoleToTeamRole(data.role)
  await supabase
    .from('profiles')
    .update({ team_role: teamRole })
    .eq('id', data.user_id)
    .eq('organization_id', orgId)

  return newRole
}

/**
 * Update a user's role
 */
export async function updateUserRole(
  roleId: string,
  updates: UserRoleUpdate
): Promise<UserRole> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) throw new Error('Unable to resolve organization')

  const { data, error } = await supabase
    .from('user_roles')
    .update(updates)
    .eq('id', roleId)
    .eq('organization_id', orgId)
    .select('*')
    .single()

  if (error) throw toError(error)
  return data
}

/**
 * Remove (deactivate) a user's role
 */
export async function removeUserRole(roleId: string): Promise<void> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) throw new Error('Unable to resolve organization')

  const { error } = await supabase
    .from('user_roles')
    .update({ is_active: false })
    .eq('id', roleId)
    .eq('organization_id', orgId)

  if (error) throw toError(error)
}

/**
 * Remove a user from the organization entirely.
 * Deactivates their role, clears their org, and resets team_role.
 */

export async function updateMemberDisplayName(userId: string, fullName: string): Promise<void> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) throw new Error('Unable to resolve organization')

  const name = fullName.trim()
  if (!name) throw new Error('Name cannot be empty')

  const { error } = await supabase
    .from('profiles')
    .update({ full_name: name })
    .eq('id', userId)
    .eq('organization_id', orgId)

  if (error) throw toError(error)
}

export async function removeFromTeam(userId: string): Promise<void> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) throw new Error('Unable to resolve organization')

  // Deactivate any active roles
  await supabase
    .from('user_roles')
    .update({ is_active: false })
    .eq('user_id', userId)
    .eq('organization_id', orgId)

  // Clear their organization_id and reset team_role
  const { error } = await supabase
    .from('profiles')
    .update({ organization_id: null, team_role: 'member' })
    .eq('id', userId)
    .eq('organization_id', orgId)

  if (error) throw toError(error)
}

/**
 * Build the invite link URL for a given invitation token.
 */
export function getInviteLink(token: string): string {
  const siteUrl = window.location.origin
  return `${siteUrl}/accept-invite?token=${token}`
}

// ============================================
// TEAM INVITATIONS
// ============================================

/**
 * Get all pending invitations
 */
export async function getTeamInvitations(): Promise<TeamInvitation[]> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return []

  const { data, error } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })

  if (error) {
    // Table may not exist yet if migration hasn't been applied
    if (error.code === 'PGRST205' || error.code === 'PGRST202' || error.code === '42P01' || error.message?.includes('permission_audit_log')) return []
    throw toError(error)
  }
  return data || []
}

/**
 * Get pending invitations only
 */
export async function getPendingInvitations(): Promise<TeamInvitation[]> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return []

  const { data, error } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('organization_id', orgId)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })

  if (error) {
    if (error.code === 'PGRST205' || error.code === 'PGRST202' || error.code === '42P01' || error.message?.includes('permission_audit_log')) return []
    throw toError(error)
  }
  return data || []
}

/**
 * Create a new team invitation
 */
export async function createTeamInvitation(
  data: TeamInvitationInsert
): Promise<TeamInvitation> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User must be authenticated')

  // Resolve organisation for RLS + NOT NULL constraints
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('organization_id')
    .eq('id', user.id)
    .single()

  if (profileError || !profile?.organization_id) {
    throw new Error('Unable to resolve organization for invitation')
  }

  const inviteeEmail = data.email?.toLowerCase().trim()
  if (!inviteeEmail) throw new Error('Email address is required')

  // P1 #10: Check if user is already an active member via accepted invitations
  const { data: activeMember } = await supabase
    .from('team_invitations')
    .select('id, accepted_by')
    .eq('organization_id', profile.organization_id)
    .eq('email', inviteeEmail)
    .eq('status', 'accepted')
    .not('accepted_by', 'is', null)
    .maybeSingle()

  if (activeMember) {
    throw new Error(`${inviteeEmail} is already a member of this team.`)
  }

  // Check for pending invitation duplicates
  const { data: existingInvite } = await supabase
    .from('team_invitations')
    .select('id, status')
    .eq('organization_id', profile.organization_id)
    .eq('email', inviteeEmail)
    .eq('status', 'pending')
    .maybeSingle()

  if (existingInvite) {
    throw new Error(`A pending invitation already exists for ${inviteeEmail}. Cancel it first or use "Resend".`)
  }

  // Enforce seat limits based on subscription tier
  const { getPlanLimits } = await import('@/lib/stripe')
  const { data: subscription } = await supabase
    .from('subscriptions')
    .select('plan_tier')
    .eq('org_id', profile.organization_id)
    .maybeSingle()

  const tier = subscription?.plan_tier || 'free_trial'
  const limits = getPlanLimits(tier)

  if (limits.users > 0) {
    // Count current members + pending invitations
    const [{ count: memberCount }, { count: pendingCount }] = await Promise.all([
      supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true),
      supabase
        .from('team_invitations')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', profile.organization_id)
        .eq('status', 'pending'),
    ])

    const totalSeats = (memberCount || 0) + (pendingCount || 0)
    if (totalSeats >= limits.users) {
      throw new Error(`Your ${tier} plan allows ${limits.users} users. You currently have ${memberCount || 0} members and ${pendingCount || 0} pending invitations. Please upgrade your plan to add more team members.`)
    }
  }

  // Generate a secure token
  const token = crypto.randomUUID()

  // Set expiry to 24 hours from now for tighter security
  const expiresAt = new Date()
  expiresAt.setHours(expiresAt.getHours() + 24)

  const { data: invitation, error } = await supabase
    .from('team_invitations')
    .insert({
      ...data,
      email: inviteeEmail, // P0 #1: always store lowercase for RLS match
      organization_id: profile.organization_id,
      invited_by: user.id,
      token,
      status: 'pending',
      expires_at: expiresAt.toISOString(),
    })
    .select('*')
    .single()

  if (error) throw toError(error)

  // P2 #18: Audit log for invitation creation (compliance requirement)
  try {
    await supabase.from('permission_audit_log').insert({
      organization_id: profile.organization_id,
      user_id: user.id,
      target_user_id: null,
      action_type: 'invitation_created',
      reason: `Invited ${inviteeEmail} as ${data.invited_role}`,
    })
  } catch {
    // Non-critical — don't block invitation creation
  }

  return dispatchTeamInvitation(invitation, 'create')
}

/**
 * Cancel an invitation
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('team_invitations')
    .update({ status: 'cancelled' })
    .eq('id', invitationId)

  if (error) throw toError(error)
}

/**
 * Resend an invitation (creates new token and expiry)
 */
export async function resendInvitation(
  invitationId: string
): Promise<TeamInvitation> {
  const token = crypto.randomUUID()
  const expiresAt = new Date()
  // Resent invites also expire in 24 hours
  expiresAt.setHours(expiresAt.getHours() + 24)

  const { data, error } = await supabase
    .from('team_invitations')
    .update({
      token,
      expires_at: expiresAt.toISOString(),
      status: 'pending',
    })
    .eq('id', invitationId)
    .select()
    .single()

  if (error) throw toError(error)

  return dispatchTeamInvitation(data, 'resend')
}

/**
 * Accept an invitation (called after user creates account)
 */
export async function acceptInvitation(token: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User must be authenticated')

  // Fetch the invitation by token — include 'accepted' for idempotency
  const { data: invitation, error: fetchError } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('token', token)
    .in('status', ['pending', 'accepted'])
    .gt('expires_at', new Date().toISOString())
    .single()

  if (fetchError || !invitation) throw new Error('Invalid or expired invitation')

  // P0 FIX #5: Verify the authenticated user's email matches the invitation
  if (invitation.email && user.email && invitation.email.toLowerCase() !== user.email.toLowerCase()) {
    throw new Error('This invitation was sent to a different email address. Please sign in with the correct account.')
  }

  // P0 FIX #10: Idempotency — if already accepted by this user, succeed silently
  if (invitation.status === 'accepted' && invitation.accepted_by === user.id) {
    return
  }

  // Check the user's current org
  const { data: currentProfile } = await supabase
    .from('profiles')
    .select('organization_id, team_role')
    .eq('id', user.id)
    .single()
  const currentOrgId = currentProfile?.organization_id

  // Skip if user is already in the inviting org (re-click / retry)
  const alreadyInOrg = currentOrgId === invitation.organization_id

  // Guard: if user is in a different org that has other members,
  // they would lose access to that org's data
  if (!alreadyInOrg && currentOrgId) {
    const { count } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', currentOrgId)

    if (count && count > 1) {
      throw new Error(
        'You are currently a member of another team. Accepting this invitation will remove you from your current team. Please contact your team admin first, or sign up with a different email address.'
      )
    }
  }

  const orphanOrgId = currentOrgId
  if (!alreadyInOrg) {
    // Transfer user to the inviting organization
    const teamRole = mapQmsRoleToTeamRole(invitation.invited_role)
    const { error: profileUpdateError } = await supabase
      .from('profiles')
      .update({
        organization_id: invitation.organization_id,
        team_role: teamRole,
      })
      .eq('id', user.id)

    if (profileUpdateError) throw profileUpdateError

    // Invalidate cached org_id since we just changed orgs
    profileService.clearOrgIdCache()
  }

  // P0 #3: Assign role BEFORE marking accepted to prevent orphaned state
  // (user in org with no active role). assignUserRole upserts, so safe to retry.
  await assignUserRole({
    user_id: user.id,
    role: invitation.invited_role,
    organization_id: invitation.organization_id,
    notes: `Assigned via invitation from ${invitation.invited_by}`,
  })

  // Mark invitation as accepted only after role is successfully assigned
  const { error: updateError } = await supabase
    .from('team_invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
      accepted_by: user.id,
    })
    .eq('id', invitation.id)

  if (updateError) throw updateError

  // Clean up orphan organization (best-effort, non-blocking)
  if (!alreadyInOrg && orphanOrgId && orphanOrgId !== invitation.organization_id) {
    try {
      const { count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', orphanOrgId)

      if (count === 0) {
        await supabase.from('organizations').delete().eq('id', orphanOrgId)
      }
    } catch {
      // Non-critical — orphan orgs can be cleaned up later
    }
  }
}

// ============================================
// PERMISSIONS
// ============================================

/**
 * Get all permissions
 */
export async function getPermissions(): Promise<Permission[]> {
  const { data, error } = await supabase
    .from('permissions')
    .select('*')
    .order('category', { ascending: true })
    .order('action', { ascending: true })

  if (error) throw toError(error)
  return data || []
}

/**
 * Get permissions for a specific role
 */
export async function getRolePermissions(role: QmsRole): Promise<Permission[]> {
  const { data, error } = await supabase
    .from('role_permissions')
    .select(`
      permission:permissions (*)
    `)
    .eq('role', role)
    .eq('granted_by_default', true)

  if (error) throw toError(error)

  // Supabase returns permission as object (singular join)
  // Cast through unknown to satisfy TypeScript
  return (data || [])
    .map((rp) => (rp.permission as unknown) as Permission | null)
    .filter((p): p is Permission => p !== null)
}

/**
 * Check if current user has a specific permission
 */
export async function checkPermission(
  permissionCode: string
): Promise<PermissionCheckResult> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    return {
      has_permission: false,
      permission_code: permissionCode,
      user_role: null,
      granted_via: null,
    }
  }

  // Call the database function
  const { data, error } = await supabase
    .rpc('user_has_permission', {
      p_user_id: user.id,
      p_permission_code: permissionCode,
    })

  if (error) throw toError(error)

  // Get user's role for the result
  const { data: roleData } = await supabase
    .rpc('get_user_qms_role', { p_user_id: user.id })

  return {
    has_permission: data || false,
    permission_code: permissionCode,
    user_role: roleData || null,
    granted_via: data ? 'role' : null,
  }
}

/**
 * Check multiple permissions at once
 */
export async function checkPermissions(
  permissionCodes: string[]
): Promise<Record<string, boolean>> {
  const results: Record<string, boolean> = {}

  for (const code of permissionCodes) {
    const result = await checkPermission(code)
    results[code] = result.has_permission
  }

  return results
}

/**
 * Get current user's QMS role
 */
export async function getCurrentUserRole(): Promise<QmsRole | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .rpc('get_user_qms_role', { p_user_id: user.id })

  if (error) {
    // RPC function may not exist yet if migration hasn't been applied
    // Fall back to reading role from profiles table
    if (error.code === 'PGRST205' || error.code === 'PGRST202' || error.code === '42883' || error.code === '42P01' || error.message?.includes('get_user_qms_role') || error.message?.includes('could not find')) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single()
      // Map legacy 'owner' role to QMS equivalent
      const legacyRole = profile?.role
      if (legacyRole === 'owner') return 'qms_owner'
      if (legacyRole === 'admin') return 'implementation_admin'
      if (legacyRole === 'viewer') return 'reviewer'
      if (legacyRole === 'member') return 'team_member'
      return (legacyRole as QmsRole) || null
    }
    throw toError(error)
  }
  return data
}

// ============================================
// PERMISSION AUDIT LOG
// ============================================

/**
 * Get permission audit log entries
 */
export async function getPermissionAuditLog(
  options: {
    limit?: number
    targetUserId?: string
  } = {}
): Promise<PermissionAuditLog[]> {
  const orgId = await profileService.getOrganizationId()
  if (!orgId) return []

  // Fetch audit log without FK joins to avoid hint issues
  let query = supabase
    .from('permission_audit_log')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })

  if (options.targetUserId) {
    query = query.eq('target_user_id', options.targetUserId)
  }

  if (options.limit) {
    query = query.limit(options.limit)
  }

  const { data, error } = await query

  if (error) {
    if (error.code === 'PGRST205' || error.code === 'PGRST202' || error.code === '42P01' || error.message?.includes('permission_audit_log')) return []
    throw toError(error)
  }

  if (!data || data.length === 0) return []

  // Fetch related profiles separately
  const profileIds = [...new Set(data.flatMap(d => [d.user_id, d.target_user_id].filter(Boolean)))]
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name')
    .in('id', profileIds)

  const profileMap = new Map((profiles || []).map(p => [p.id, p]))

  return data.map(entry => {
    const userProfile = entry.user_id ? profileMap.get(entry.user_id) : null
    const targetProfile = entry.target_user_id ? profileMap.get(entry.target_user_id) : null
    return {
      ...entry,
      user: userProfile ? { id: userProfile.id, full_name: userProfile.full_name, email: null } : null,
      target_user: targetProfile ? { id: targetProfile.id, full_name: targetProfile.full_name, email: null } : null,
    }
  })
}

// ============================================
// NOTIFICATION PREFERENCES
// ============================================

/**
 * Get notification preferences for a user
 */
export async function getNotificationPreferences(
  userId?: string
): Promise<NotificationPreferences | null> {
  const { data: { user } } = await supabase.auth.getUser()
  const targetUserId = userId || user?.id

  if (!targetUserId) return null

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', targetUserId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw toError(error)
  }

  return data
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  updates: NotificationPreferencesUpdate
): Promise<NotificationPreferences> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User must be authenticated')

  // Try to update existing preferences
  const { data: existing } = await supabase
    .from('notification_preferences')
    .select('id')
    .eq('user_id', user.id)
    .single()

  if (existing) {
    const { data, error } = await supabase
      .from('notification_preferences')
      .update(updates)
      .eq('user_id', user.id)
      .select()
      .single()

    if (error) throw toError(error)
    return data
  }

  // Create new preferences
  const { data, error } = await supabase
    .from('notification_preferences')
    .insert({
      user_id: user.id,
      ...updates,
    })
    .select()
    .single()

  if (error) throw toError(error)
  return data
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if user can assign a specific role
 * QMS Owners can assign any role
 * Implementation Admins can assign most roles except QMS Owner
 */
export function canAssignRole(
  userRole: QmsRole | null,
  targetRole: QmsRole
): boolean {
  if (!userRole) return false

  if (userRole === 'qms_owner') {
    return true // Can assign any role
  }

  if (userRole === 'implementation_admin') {
    return targetRole !== 'qms_owner' // Can't assign QMS Owner
  }

  return false
}

/**
 * Get role hierarchy level (higher = more permissions)
 */
export function getRoleLevel(role: QmsRole): number {
  const levels: Record<QmsRole, number> = {
    qms_owner: 100,
    implementation_admin: 90,
    internal_auditor: 60,
    process_owner: 50,
    team_member: 30,
    reviewer: 10,
  }
  return levels[role]
}
